export type LinkedinUrlType = "PERSON" | "COMPANY";

export interface ClassifiedLinkedinUrl {
	type: LinkedinUrlType;
	/** Normalized `https://www.linkedin.com/...` URL (query/hash/trailing slash stripped). */
	url: string;
}

const PERSON_SEGMENTS = ["in", "pub"];
const COMPANY_SEGMENTS = ["company", "school", "showcase"];

/**
 * Classifies a typed/pasted value as a LinkedIn person or company URL, or `null` when it is
 * not a LinkedIn URL (i.e. a plain search keyword). Lets the BY_PROFILE inputs switch from
 * CompanyEnrich search to direct URL resolution — the backend re-validates before resolving.
 */
export const classifyLinkedinUrl = (raw: string): ClassifiedLinkedinUrl | null => {
	const trimmed = raw?.trim();

	if (!trimmed) {
		return null;
	}

	const url = safeParseUrl(trimmed);

	if (!url || !/(^|\.)linkedin\.com$/i.test(url.hostname)) {
		return null;
	}

	const [segment, handle] = url.pathname.split("/").filter(Boolean);

	if (!segment || !handle) {
		return null;
	}

	const normalizedSegment = segment.toLowerCase();
	const type = classifySegment(normalizedSegment);

	if (!type) {
		return null;
	}

	return { type, url: `https://www.linkedin.com/${normalizedSegment}/${handle}` };
};

const classifySegment = (segment: string): LinkedinUrlType | null => {
	if (PERSON_SEGMENTS.includes(segment)) {
		return "PERSON";
	}

	if (COMPANY_SEGMENTS.includes(segment)) {
		return "COMPANY";
	}

	return null;
};

const safeParseUrl = (value: string): URL | null => {
	const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;

	try {
		return new URL(withProtocol);
	} catch {
		return null;
	}
};
